/**
 * Shows a PDF generated in the browser, in a new tab.
 *
 * Call it at the START of the click handler, before any `await`: it opens the
 * tab right away and only then runs `generate`. A browser lets a page open a tab
 * only within the user's click; after the awaits of loading a renderer, reading
 * data and rendering (Safari: any await; Chrome: ~5 s) the same `window.open`
 * is an unrequested popup and is blocked SILENTLY — the button looks broken.
 * That is what the transcript, the syllabus preview and the CAP documents did.
 *
 * If the browser blocks even the immediate tab, the PDF is downloaded instead.
 * If `generate` throws, the empty tab is closed and the error is rethrown for the
 * caller to show.
 */
export async function openGeneratedPdf(
  generate: () => Promise<Blob>,
  filename: string,
): Promise<void> {
  const tab = window.open('', '_blank');
  let blob: Blob;
  try {
    blob = await generate();
  } catch (err) {
    tab?.close();
    throw err;
  }
  const url = URL.createObjectURL(blob);
  if (tab) {
    // No 'noopener' on the open above: it would make window.open return null.
    // The opener link is cut here instead.
    tab.opener = null;
    tab.location.href = url;
    return;
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
